import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography, Box, TextField, Button, Avatar, CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CarRentalIcon from '@mui/icons-material/CarRental';
import GroupIcon from '@mui/icons-material/Group';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import authService from '../../services/auth.service';
import partnerService from '../../services/partner.service';
import { useAuth } from '../../context/AuthContext';

const USER_TYPE_LABELS = {
  client: 'Client',
  driver: 'Chauffeur VTC',
  delivery: 'Livreur',
  restaurant_owner: 'Restaurateur',
  admin: 'Administrateur',
};

/* -- Palette raffinee : tons mats organiques, pas de degrades cliches -- */
const C = {
  purple: '#552583',
  purpleDark: '#33154f',
  gold: '#FDB927',
  bg: '#E4F0F6',
  surface: '#E4F0F6',
  white: '#ffffff',
  text: '#1c1917',
  textSoft: '#44403c',
  textMuted: '#78716c',
  borderLight: 'rgba(28,25,23,0.06)',
  borderMed: 'rgba(28,25,23,0.1)',
};

/* -- Texture grain subtile -- */
const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E")`;

const SERVICES = [
  { title: 'Restaurants', desc: 'Commandes & livraison', icon: RestaurantIcon, path: '/restaurants', available: true },
  { title: 'Location', desc: 'Véhicules entre particuliers', icon: CarRentalIcon, path: '/car-rental', available: true, badge: 'Particuliers' },
  { title: 'Covoiturage', desc: 'Trajets partagés', icon: GroupIcon, path: '/carpool', available: true },
  { title: 'VTC', desc: 'Courses avec chauffeur', icon: DirectionsCarIcon, path: '/vtc', available: false },
  { title: 'Logistique', desc: 'Colis & marchandises', icon: LocalShippingIcon, path: '/logistics', available: false },
  { title: 'Courses', desc: 'Shopping à domicile', icon: ShoppingCartIcon, path: '/courses', available: false },
];

/* -- Input minimaliste : bordure fine, fond transparent, focus net -- */
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: 'transparent',
    color: C.text,
    fontSize: '0.88rem',
    '& fieldset': { borderColor: C.borderMed, borderWidth: '0.5px' },
    '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.3)' },
    '&.Mui-focused fieldset': { borderColor: C.purple, borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': { color: C.textMuted, fontSize: '0.84rem', fontWeight: 500 },
  '& .MuiInputLabel-root.Mui-focused': { color: C.purple },
};

const Profile = () => {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState({ first_name: '', last_name: '', phone: '', profile_image: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [partnerApp, setPartnerApp] = useState(null);

  useEffect(() => { loadProfile(); loadPartnerApp(); }, []);

  const loadProfile = async () => {
    try {
      const data = await authService.getProfile();
      const u = data.user;
      setProfile({ first_name: u.first_name, last_name: u.last_name, phone: u.phone, profile_image: u.profile_image || '' });
      setAvatarPreview(u.profile_image || '');
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadPartnerApp = async () => {
    try {
      const data = await partnerService.getMyApplication();
      if (data.application) setPartnerApp(data.application);
    } catch (err) { /* normal si pas de demande */ }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAvatarPreview(ev.target.result);
      setProfile(p => ({ ...p, profile_image: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    setSaving(true);
    try {
      await authService.updateProfile(profile);
      updateUser({ ...user, ...profile });
      setMessage('Modifications enregistrées');
    } catch (err) { setError(err.response?.data?.message || 'Erreur lors de la mise à jour'); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    if (passwords.new_password !== passwords.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setSavingPwd(true);
    try {
      await authService.changePassword(passwords.current_password, passwords.new_password);
      setMessage('Mot de passe modifié');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.passwordLeaked
        ? 'Ce mot de passe figure dans une fuite de données.'
        : err.response?.data?.message || 'Erreur lors du changement');
    } finally { setSavingPwd(false); }
  };

  const initials = `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase();

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ background: C.bg }}>
      <CircularProgress size={24} sx={{ color: C.purple }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: C.bg, position: 'relative' }}>
      {/* Grain overlay */}
      <Box sx={{ position: 'fixed', inset: 0, backgroundImage: GRAIN, backgroundRepeat: 'repeat', backgroundSize: '200px', pointerEvents: 'none', zIndex: 0 }} />

      {/* -- En-tete editorial : asymetrique, typographie forte -- */}
      <Box sx={{ position: 'relative', zIndex: 1, pt: { xs: 5, md: 7 }, pb: { xs: 4, md: 6 } }}>
        <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 3, sm: 5, md: 6 } }}>

          {/* Avatar + meta */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2.5, md: 3.5 }, mb: { xs: 3, md: 0 } }}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar src={avatarPreview || undefined} sx={{
                width: { xs: 72, sm: 88 }, height: { xs: 72, sm: 88 },
                background: C.purple,
                fontWeight: 700, fontSize: { xs: '1.3rem', sm: '1.6rem' },
                color: C.gold,
                border: `2px solid ${C.bg}`,
                boxShadow: '0 2px 16px rgba(28,25,23,0.12)',
              }}>
                {!avatarPreview && (initials || <PersonIcon sx={{ fontSize: 32 }} />)}
              </Avatar>
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  position: 'absolute', bottom: -2, right: -2,
                  width: 26, height: 26, borderRadius: '50%',
                  background: C.text, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `2px solid ${C.bg}`,
                  transition: 'transform 0.15s',
                  '&:hover': { transform: 'scale(1.12)' },
                }}
              >
                <CameraAltIcon sx={{ fontSize: 12, color: C.white }} />
              </Box>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
            </Box>

            <Box sx={{ pt: 0.5 }}>
              {/* Nom -- typographie editoriale : large, serree */}
              <Typography sx={{
                color: C.text,
                fontWeight: 800,
                fontSize: { xs: '1.6rem', sm: '2.1rem' },
                letterSpacing: '-0.035em',
                lineHeight: 1.05,
                mb: 0.5,
              }}>
                {profile.first_name}<br />{profile.last_name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Typography sx={{ color: C.textMuted, fontSize: '0.78rem', letterSpacing: '0.01em' }}>
                  {user?.email}
                </Typography>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: C.textMuted, opacity: 0.4 }} />
                <Typography sx={{
                  color: C.purple, fontSize: '0.68rem', fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                  {USER_TYPE_LABELS[user?.user_type] || user?.user_type}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* -- Contenu principal -- */}
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 3, sm: 5, md: 6 }, pb: 12, position: 'relative', zIndex: 1 }}>

        {/* Feedback alerts */}
        {message && (
          <Box sx={{
            mb: 4, py: 1.5, px: 2.5,
            border: '0.5px solid rgba(34,197,94,0.3)',
            borderRadius: '8px',
            background: 'rgba(34,197,94,0.06)',
            fontSize: '0.82rem', fontWeight: 600, color: '#15803d',
          }}>
            {message}
          </Box>
        )}
        {error && (
          <Box sx={{
            mb: 4, py: 1.5, px: 2.5,
            border: '0.5px solid rgba(239,68,68,0.25)',
            borderRadius: '8px',
            background: 'rgba(239,68,68,0.05)',
            fontSize: '0.82rem', fontWeight: 600, color: '#b91c1c',
          }}>
            {error}
          </Box>
        )}

        {/* Statut demande partenaire */}
        {partnerApp && (
          <Box sx={{
            mb: 5, py: 2, px: 3, borderRadius: '8px',
            border: `0.5px solid ${partnerApp.status === 'pending' ? 'rgba(253,185,39,0.35)' : partnerApp.status === 'approved' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.25)'}`,
            background: partnerApp.status === 'pending' ? 'rgba(253,185,39,0.05)' : partnerApp.status === 'approved' ? 'rgba(34,197,94,0.04)' : 'rgba(239,68,68,0.04)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              {partnerApp.status === 'pending' && <HourglassTopIcon sx={{ color: '#b45309', fontSize: 18 }} />}
              {partnerApp.status === 'approved' && <CheckCircleOutlineIcon sx={{ color: '#15803d', fontSize: 18 }} />}
              {partnerApp.status === 'rejected' && <CancelOutlinedIcon sx={{ color: '#b91c1c', fontSize: 18 }} />}
              <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: C.text }}>
                {partnerApp.status === 'pending' && 'Vérification en cours'}
                {partnerApp.status === 'approved' && 'Compte partenaire actif'}
                {partnerApp.status === 'rejected' && 'Demande refusée'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.76rem', color: C.textMuted, pl: 4.2 }}>
              {partnerApp.status === 'pending' && 'Votre dossier est en cours d\'examen. Réponse sous 24-48h par email.'}
              {partnerApp.status === 'approved' && 'Accédez à votre espace depuis le menu.'}
              {partnerApp.status === 'rejected' && (partnerApp.review_note || 'Contactez-nous pour plus de détails.')}
            </Typography>
          </Box>
        )}

        {/* -- Layout asymetrique : large gauche / etroit droite -- */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 280px' },
          gap: { xs: 5, md: 7 },
          alignItems: 'start',
        }}>

          {/* -- Colonne gauche : formulaires -- */}
          <Box>

            {/* Section : Informations */}
            <Box sx={{ mb: 6 }}>
              <Typography sx={{
                color: C.textMuted, fontSize: '0.62rem', fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase', mb: 3,
              }}>
                Informations personnelles
              </Typography>

              <form onSubmit={handleProfileUpdate}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, mb: 3 }}>
                  <TextField fullWidth label="Prénom" value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    required sx={fieldSx} />
                  <TextField fullWidth label="Nom" value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    required sx={fieldSx} />
                  <TextField fullWidth label="Téléphone" value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    required sx={fieldSx} />
                  <TextField fullWidth label="Email" value={user?.email || ''} disabled
                    sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], opacity: 0.45 } }} />
                </Box>
                <Button type="submit" disabled={saving}
                  sx={{
                    py: 1.2, px: 3.5,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 700, fontSize: '0.82rem',
                    background: C.text,
                    color: C.white,
                    boxShadow: 'none',
                    letterSpacing: '0.01em',
                    transition: 'opacity 0.15s',
                    '&:hover': { background: C.text, opacity: 0.85 },
                    '&:disabled': { opacity: 0.3, background: C.text, color: C.white },
                  }}>
                  {saving ? 'Enregistrement...' : 'Sauvegarder'}
                </Button>
              </form>
            </Box>

            {/* Separateur fin */}
            <Box sx={{ height: '0.5px', background: C.borderMed, mb: 6 }} />

            {/* Section : Securite */}
            <Box sx={{ mb: 6 }}>
              <Typography sx={{
                color: C.textMuted, fontSize: '0.62rem', fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase', mb: 3,
              }}>
                Sécurité
              </Typography>

              <form onSubmit={handlePasswordChange}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                  <TextField fullWidth label="Actuel" type="password"
                    value={passwords.current_password}
                    onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                    required sx={fieldSx} />
                  <TextField fullWidth label="Nouveau" type="password"
                    value={passwords.new_password}
                    onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                    required sx={fieldSx} />
                  <TextField fullWidth label="Confirmer" type="password"
                    value={passwords.confirm_password}
                    onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                    required sx={fieldSx} />
                </Box>

                <Button type="submit" disabled={savingPwd} variant="outlined"
                  sx={{
                    py: 1, px: 2.5,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600, fontSize: '0.8rem',
                    borderColor: C.borderMed, borderWidth: '0.5px',
                    color: C.textSoft,
                    '&:hover': { borderColor: C.textMuted, background: 'rgba(28,25,23,0.02)' },
                    '&:disabled': { opacity: 0.3 },
                  }}>
                  {savingPwd ? 'Modification...' : 'Modifier le mot de passe'}
                </Button>
              </form>
            </Box>

            {/* Separateur fin */}
            <Box sx={{ height: '0.5px', background: C.borderMed, mb: 6 }} />

            {/* Section : Adresses */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{
                    color: C.textMuted, fontSize: '0.62rem', fontWeight: 700,
                    letterSpacing: '0.14em', textTransform: 'uppercase', mb: 0.8,
                  }}>
                    Adresses
                  </Typography>
                  <Typography sx={{ color: C.textSoft, fontSize: '0.82rem', lineHeight: 1.6 }}>
                    Gérez vos adresses de livraison et de facturation.
                  </Typography>
                </Box>
                <Button component={Link} to="/profile/addresses"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '0.75rem !important' }} />}
                  sx={{
                    py: 0.8, px: 2,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600, fontSize: '0.78rem',
                    borderColor: C.borderMed, borderWidth: '0.5px',
                    color: C.textSoft,
                    border: `0.5px solid ${C.borderMed}`,
                    '&:hover': { borderColor: C.textMuted, background: 'rgba(28,25,23,0.02)' },
                  }}>
                  Gérer
                </Button>
              </Box>

              {/* -- Moyens de paiement -- */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pt: 2.5, borderTop: `0.5px solid ${C.borderLight}` }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: 'rgba(85,37,131,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#552583" strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: C.text, mb: 0.3 }}>
                      Moyens de paiement
                    </Typography>
                    <Typography sx={{ color: C.textSoft, fontSize: '0.82rem', lineHeight: 1.6 }}>
                      Gérez vos cartes enregistrées pour payer plus vite.
                    </Typography>
                  </Box>
                </Box>
                <Button component={Link} to="/profile/payment-methods"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '0.75rem !important' }} />}
                  sx={{
                    py: 0.8, px: 2,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600, fontSize: '0.78rem',
                    color: C.textSoft,
                    border: `0.5px solid ${C.borderMed}`,
                    '&:hover': { borderColor: C.textMuted, background: 'rgba(28,25,23,0.02)' },
                  }}>
                  Gérer
                </Button>
              </Box>
            </Box>
          </Box>

          {/* -- Colonne droite : navigation services (compacte, sticky) -- */}
          <Box sx={{ position: { md: 'sticky' }, top: { md: 32 } }}>
            <Typography sx={{
              color: C.textMuted, fontSize: '0.58rem', fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2.5,
            }}>
              Services
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {SERVICES.map(({ title, desc, icon: Icon, path, available, badge }, idx) => (
                <Box
                  key={title}
                  component={available ? Link : 'div'}
                  to={available ? path : undefined}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.8,
                    py: 1.6,
                    borderTop: idx === 0 ? `0.5px solid ${C.borderLight}` : 'none',
                    borderBottom: `0.5px solid ${C.borderLight}`,
                    textDecoration: 'none',
                    opacity: available ? 1 : 0.35,
                    cursor: available ? 'pointer' : 'default',
                    transition: 'all 0.15s',
                    ...(available && {
                      '&:hover': { pl: 0.5 },
                      '&:hover .svc-arrow': { opacity: 1, transform: 'translateX(0)' },
                    }),
                  }}
                >
                  <Icon sx={{ color: available ? C.purple : C.textMuted, fontSize: 18, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: C.text, fontWeight: 600, fontSize: '0.82rem' }}>
                        {title}
                      </Typography>
                      {badge && (
                        <Typography sx={{
                          fontSize: '0.55rem', fontWeight: 700,
                          color: C.gold, letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                        }}>
                          {badge}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>
                      {desc}
                    </Typography>
                  </Box>
                  {available ? (
                    <ArrowForwardIcon className="svc-arrow" sx={{
                      fontSize: 14, color: C.textMuted,
                      opacity: 0, transform: 'translateX(-4px)',
                      transition: 'all 0.15s',
                    }} />
                  ) : (
                    <Typography sx={{
                      fontSize: '0.58rem', fontWeight: 600,
                      color: C.textMuted, letterSpacing: '0.04em',
                    }}>
                      Bientôt
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* CTA location -- traitement editorial */}
            <Box sx={{ mt: 4, pt: 3, borderTop: `0.5px solid ${C.borderLight}` }}>
              <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.88rem', mb: 0.5, letterSpacing: '-0.01em' }}>
                Vous avez un véhicule ?
              </Typography>
              <Typography sx={{ color: C.textMuted, fontSize: '0.74rem', lineHeight: 1.6, mb: 2 }}>
                Mettez-le en location et générez des revenus. Accessible aux particuliers.
              </Typography>
              <Button
                component={Link} to="/car-rental/list"
                sx={{
                  py: 1, px: 2.5,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 700, fontSize: '0.76rem',
                  background: C.purple,
                  color: C.white,
                  boxShadow: 'none',
                  letterSpacing: '0.01em',
                  '&:hover': { background: C.purpleDark },
                }}>
                Proposer mon véhicule
              </Button>
            </Box>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default Profile;
